<template>
  <div class="item-form-page">
    <!-- Sticky Actions Bar -->
    <div class="item-form-page__actions-bar pr-4">
      <v-alert v-if="errorMessage" type="error" dense dismissible class="mb-2 flex-grow-1" @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
      <div class="d-flex justify-space-between align-center">
        <span class="text-styled-h3 white--text pa-0 ma-0">Editing {{ draft.name }}</span>
        <div class="d-flex justify-end align-center">

          <v-btn light color="amber" class="mr-2" @click="handleSubmit">
            {{ isEditing ? 'Update' : 'Create' }}
          </v-btn>
          <v-btn light color="grey" @click="handleCancel">
            Cancel
          </v-btn>
        </div>
      </div>
    </div>

    <v-row>
      <!-- Left Column: Basic Info -->
      <v-col cols="6">
        <p class="text-styled-h4 white--text">Details</p>
        <v-text-field v-model="draft.name" label="Title *" placeholder="Name" color="amber" required
          :error="errors.name" :error-messages="errors.name ? 'Title is required' : ''" @input="clearError('name')" />

        <v-text-field v-model="draft.sentence" label="Sentence" placeholder="In a sentence" color="amber" required />

        <!-- Single group selection - card can only be in one group (required) -->
        <v-select v-model="draft.group" :items="groupOptions" label="Group *" item-text="title" item-value="id"
          :rules="groupRules" required class="mb-4" :error="errors.group" @change="clearError('group')" />

        <v-textarea v-model="draft.body" label="Narrative" placeholder="Narrative" color="amber" :rows="2" auto-grow
          hide-details class="mb-4" />

        <v-textarea v-model="draft.historical" label="Description" placeholder="Description" color="amber" :rows="2"
          auto-grow hide-details class="mb-4" />

        <!-- <v-select v-model="draft.color" :items="colorOptions" label="Colour code" item-text="title" item-value="color"
          class="mr-4 mt-4" /> -->

        <div class="mt-14" v-if="doc.contribution">
          <p class="text-styled-h4 white--text">How close is the offer to the masterbrand?</p>
          <v-slider v-model="draft.stretch" class="align-center" label="Brand stretch" color="amber" :max="1" :min="10"
            ticks="always" tick-size="4" thumb-color="amber" track-color="amber accent-1"></v-slider>

          <p class="text-styled-h4 white--text">Contribution to the business</p>
          <v-slider v-model="draft.contribution" class="align-center" label="Contribution" color="amber" :max="1"
            :min="10" ticks="always" tick-size="4" thumb-color="amber" track-color="amber accent-1"></v-slider>

          <p class="text-styled-h4 white--text">Importance for reputation</p>
          <v-slider v-model="draft.reputation" class="align-center" label="Reputation" color="amber" :max="1" :min="10"
            ticks="always" tick-size="4" thumb-color="amber" track-color="amber accent-1"></v-slider>
        </div>

        <div class="mt-8" v-if="doc.stats">
          <p class="text-styled-h4 white--text">Asset class mix</p>
          <v-row class="mb-8">
            <v-col v-for="score in scoreFields" :key="score.key" cols="6">
              <p class="ma-0 pa-0">{{ score.label }}</p>
              <v-slider v-model="draft.scores[score.key]" :min="0" :max="5" step="1" ticks="always" tick-size="4"
                color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details />
            </v-col>
          </v-row>
        </div>

      </v-col>

      <!-- Middle Column: Scores -->

      <v-col cols="6">

        <p class="text-styled-h4 white--text">Categories</p>
        <v-select v-model="draft.categories" :items="categoryOptions" label="Categories" item-text="title"
          item-value="id" multiple />

        <v-select v-model="draft.assets" :items="assetOptions" label="Assets" item-text="title" item-value="id"
          multiple />

        <v-select v-model="draft.tags" :items="tagOptions" label="Tags" item-text="title" item-value="id" multiple />

        <v-select v-model="draft.audiences" :items="audienceOptions" label="Audiences" item-text="title" item-value="id"
          multiple />

        <div class="mt-8">
          <p class="text-styled-h4 white--text">Features and Benefits</p>
          <unit-sub-items v-model="draft.features" name="sub" class="mb-4">
            <v-label>
              <span class="black--text">Define the features of the offer</span>
            </v-label>
            <p class="ma-0">Type below and press <strong>Add</strong></p>
          </unit-sub-items>

          <unit-sub-items v-model="draft.benefits" name="sub">
            <v-label>
              <span class="black--text">Define the benefits of the offer</span>
            </v-label>
            <p class="ma-0">Type below and press <strong>Add</strong></p>
          </unit-sub-items>
        </div>

      </v-col>

    </v-row>
  </div>
</template>

<script>
import { UnitSubItems } from '@/components/units'
import { itemModel, colors, scoreFields, deepClone } from './model'

/**
 * Build a draft object from an existing item (or empty)
 * @param {Object|null} existingItem - Existing item data to edit
 * @param {string|null} defaultGroup - Default group ID for new items
 */
const buildDraft = (existingItem, defaultGroup = null) => {
  const base = deepClone(itemModel)

  // For new items, apply default group if provided
  if (!existingItem) {
    if (defaultGroup) {
      base.group = defaultGroup
    }
    return base
  }

  // Merge existing item data into base
  const merged = { ...base }
  for (const key of Object.keys(existingItem)) {
    if (existingItem[key] !== undefined) {
      merged[key] = deepClone(existingItem[key])
    }
  }

  // Backward compatibility: convert legacy groups array to single group
  if (Array.isArray(existingItem.groups) && existingItem.groups.length > 0) {
    merged.group = existingItem.groups[0]
  }

  return merged
}

export default {
  name: 'ItemFormPage',

  components: {
    UnitSubItems,
  },

  props: {
    // Parent doc containing local options (categories, tags, etc.)
    doc: {
      type: Object,
      default: () => ({}),
    },

    // Existing item to edit (null for new)
    item: {
      type: Object,
      default: null,
    },

    // Default group ID for new items (pre-populates the group dropdown)
    defaultGroup: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      draft: buildDraft(this.item, this.defaultGroup),
      colorOptions: colors,
      scoreFields: scoreFields,
      groupRules: [(v) => !!v || 'Group is required'],
      errorMessage: '',
      errors: {
        name: false,
        group: false,
      },
    }
  },

  computed: {
    isEditing() {
      return !!(this.item && (this.item.id || this.item.uid))
    },

    // Safe accessors for doc.local arrays
    groupOptions() {
      return this.doc?.list || []
    },

    categoryOptions() {
      return this.doc?.local?.categories || []
    },

    assetOptions() {
      return this.doc?.local?.assets || []
    },

    tagOptions() {
      return this.doc?.local?.tags || []
    },

    audienceOptions() {
      return this.doc?.local?.audiences || []
    },
  },

  watch: {
    // Reset draft when item prop changes
    item: {
      handler(newItem) {
        this.draft = buildDraft(newItem, this.defaultGroup)
      },
      immediate: false,
    },

    // Update draft group when defaultGroup changes (for new items only)
    defaultGroup: {
      handler(newDefaultGroup) {
        // Only apply default group if not editing and no group is set
        if (!this.isEditing && !this.draft.group && newDefaultGroup) {
          this.draft.group = newDefaultGroup
        }
      },
      immediate: false,
    },
  },

  methods: {
    handleSubmit() {
      // Reset errors
      this.errorMessage = ''
      this.errors = { name: false, group: false }

      // Collect validation errors
      const validationErrors = []

      if (!this.draft.name?.trim()) {
        this.errors.name = true
        validationErrors.push('Title is required')
      }

      if (!this.draft.group) {
        this.errors.group = true
        validationErrors.push('Group is required')
      }

      // Show error message if validation failed
      if (validationErrors.length > 0) {
        this.errorMessage = validationErrors.join('. ')
        return
      }

      this.$emit('submit', deepClone(this.draft))
    },

    handleCancel() {
      this.$emit('cancel')
    },

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false
      }
      // Clear global error message when user starts fixing
      if (this.errorMessage) {
        this.errorMessage = ''
      }
    },

    reset() {
      this.draft = buildDraft(this.item, this.defaultGroup)
      this.errorMessage = ''
      this.errors = { name: false, group: false }
    },
  },
}
</script>

<style lang="scss" scoped>
.item-form-page {
  padding: 0;

  &__actions-bar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #1e1e1e;
    padding: 12px 0;
    margin-bottom: 16px;
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>